import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Square } from "lucide-react";
import { Chapter, MaskedLines, Fade, EASE } from "./motion";

const YEARS = [
    {
        id: "01",
        tag: "YEAR 01",
        name: "Foundation",
        focus: "Core engineering fundamentals, structured communication, and learning discipline. The base layer everything else stands on.",
        milestones: [
            "Capability baseline & diagnostic mapping",
            "AI Communication Lab onboarding",
            "Structured weekly practice cycles",
        ],
        outcomes: ["Verified capability map", "Communication fundamentals", "Learning discipline"],
        tech: ["AI Communication Lab", "24/7 Practice Engine", "Diagnostics"],
    },
    {
        id: "02",
        tag: "YEAR 02",
        name: "Skill & Presence",
        focus: "Domain depth meets professional presence. Students earn their first industry credential and begin building public proof of work.",
        milestones: [
            "Industry certification track 01",
            "Portfolio v1 — proof of work",
            "Presentation & interview cycles",
        ],
        outcomes: ["Industry Certification 01", "Live professional portfolio", "Interview-ready presence"],
        tech: ["AI Interviewer", "Portfolio Builder", "Practice Engine"],
    },
    {
        id: "03",
        tag: "YEAR 03",
        name: "Application",
        focus: "Real work, real stakes. Knowledge converts into experience through internships, live projects, and publishable research.",
        milestones: [
            "Practical internship 01",
            "Real-world industry project",
            "Technical paper development",
            "Industry certification track 02",
        ],
        outcomes: ["Internship 01 completed", "Industry Certification 02", "Project shipped"],
        tech: ["AI Interviewer — Advanced", "Practice Telemetry", "Research Support"],
    },
    {
        id: "04+",
        tag: "YEAR 04+",
        name: "Placement",
        focus: "Placement readiness and industry exposure converge into the final transition — from campus to career.",
        milestones: [
            "Practical internship 02",
            "Published technical paper",
            "Full interview simulation loops",
            "IP & patent evaluation",
        ],
        outcomes: ["Published technical paper", "Internship 02 completed", "Placement-ready profile"],
        tech: ["AI Interviewer — Placement", "Portfolio Builder — Final", "IP & Patent Support"],
    },
];

const Block = ({ title, children }) => (
    <div>
        <p className="mb-5 text-[10px] uppercase tracking-[0.3em] text-slate">{title}</p>
        {children}
    </div>
);

export default function Journey() {
    const [active, setActive] = useState(0);
    const year = YEARS[active];

    return (
        <section id="journey" className="bg-paper py-20 md:py-40" data-testid="journey-section">
            <div className="mx-auto max-w-[1440px] px-6 md:px-10 lg:px-16">
                <Chapter index="CH.04" title="The Four-Year Journey" />

                <div className="mt-16 md:mt-24">
                    <MaskedLines
                        stagger={0.13}
                        lineClassName="font-display text-[clamp(2.2rem,7.5vw,5.4rem)] font-semibold leading-[1.12] tracking-[-0.02em] text-ink"
                        lines={[
                            <span key="a">Four years.</span>,
                            <span key="b" className="text-cobalt">One trajectory.</span>,
                        ]}
                    />
                </div>

                <Fade delay={0.25} className="mt-16 md:mt-20">
                    <div className="grid grid-cols-2 gap-px border border-ink/10 bg-ink/10 lg:grid-cols-4" role="tablist" aria-label="Select year">
                        {YEARS.map((y, i) => (
                            <button
                                key={y.id}
                                data-testid={`journey-year-tab-${i + 1}`}
                                role="tab"
                                aria-selected={active === i}
                                onClick={() => setActive(i)}
                                className={`px-5 py-6 text-left transition-colors duration-300 md:px-7 ${
                                    active === i
                                        ? "bg-cobalt text-white"
                                        : "bg-white text-ink hover:bg-mist"
                                }`}
                            >
                                <span className={`block text-[10px] uppercase tracking-[0.25em] ${active === i ? "text-sky" : "text-slate"}`}>
                                    {y.tag}
                                </span>
                                <span className="mt-1.5 block font-display text-lg font-semibold tracking-tight md:text-xl">
                                    {y.name}
                                </span>
                            </button>
                        ))}
                    </div>
                    <div className="relative mt-6 h-px bg-ink/10">
                        <motion.div
                            className="absolute left-0 top-0 h-px bg-cobalt"
                            animate={{ width: `${((active + 1) / YEARS.length) * 100}%` }}
                            transition={{ duration: 0.7, ease: EASE }}
                        />
                        <motion.div
                            className="absolute -top-[3px] h-[7px] w-[7px] bg-cobalt"
                            animate={{ left: `calc(${((active + 1) / YEARS.length) * 100}% - 7px)` }}
                            transition={{ duration: 0.7, ease: EASE }}
                        />
                    </div>
                </Fade>

                <div className="mt-10 md:mt-14">
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={year.id}
                            initial={{ opacity: 0, y: 24 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -16 }}
                            transition={{ duration: 0.55, ease: EASE }}
                            className="relative border border-ink/10 bg-white p-6 md:p-12 lg:p-16"
                            data-testid="journey-panel"
                        >
                            <span className="absolute -left-px -top-px h-3 w-3 border-l-2 border-t-2 border-cobalt" />
                            <span className="absolute -bottom-px -right-px h-3 w-3 border-b-2 border-r-2 border-cobalt" />

                            <div className="grid gap-12 lg:grid-cols-12">
                                <div className="lg:col-span-4">
                                    <p className="text-[10px] uppercase tracking-[0.3em] text-slate">{year.tag} — Focus</p>
                                    <p className="mt-4 font-display text-[clamp(4.5rem,9vw,8rem)] font-extrabold leading-none tracking-tight text-cobalt">
                                        {year.id}
                                    </p>
                                    <p className="mt-6 max-w-sm text-base leading-relaxed text-slate">
                                        {year.focus}
                                    </p>
                                </div>
                                <div className="grid gap-10 border-t border-ink/10 pt-10 sm:grid-cols-2 lg:col-span-8 lg:grid-cols-3 lg:border-l lg:border-t-0 lg:pl-12 lg:pt-0">
                                    <Block title="Milestones">
                                        <ul className="space-y-3.5">
                                            {year.milestones.map((m) => (
                                                <li key={m} className="flex items-start gap-2.5 text-sm leading-relaxed text-ink">
                                                    <Plus className="mt-0.5 h-3.5 w-3.5 shrink-0 text-cobalt" strokeWidth={2.5} />
                                                    {m}
                                                </li>
                                            ))}
                                        </ul>
                                    </Block>
                                    <Block title="Student Outcomes">
                                        <ul className="space-y-3.5">
                                            {year.outcomes.map((o) => (
                                                <li key={o} className="flex items-start gap-2.5 text-sm leading-relaxed text-ink">
                                                    <Square className="mt-1 h-3 w-3 shrink-0 fill-sky text-sky" />
                                                    {o}
                                                </li>
                                            ))}
                                        </ul>
                                    </Block>
                                    <Block title="QRUS Technology">
                                        <div className="flex flex-wrap gap-2">
                                            {year.tech.map((t) => (
                                                <span key={t} className="border border-cobalt/30 px-3 py-1.5 text-[10px] font-medium uppercase tracking-[0.15em] text-cobalt">
                                                    {t}
                                                </span>
                                            ))}
                                        </div>
                                    </Block>
                                </div>
                            </div>

                            <div className="mt-12 flex items-center justify-between border-t border-ink/10 pt-6">
                                <span className="text-[10px] uppercase tracking-[0.3em] text-slate">
                                    FIG.04 — Engineering Roadmap
                                </span>
                                <span className="text-[10px] uppercase tracking-[0.3em] text-cobalt">
                                    {year.tag} → {active < YEARS.length - 1 ? YEARS[active + 1].tag : "CAREER"}
                                </span>
                            </div>
                        </motion.div>
                    </AnimatePresence>
                </div>
            </div>
        </section>
    );
}
